/**
 * Render a weight or volume exactly as the data carries it.
 *
 * Part weights and volumes arrive from the ERP at up to 6 decimals and are
 * stored at that precision (`PartCatalog`), so the screen shows the value as
 * sent — never rounded to a fixed number of places, and never padded with the
 * trailing zeros the API's fixed-scale decimal strings carry ("0.004500" reads
 * "0.0045", "180.000000" reads "180").
 *
 * A computed figure (quantity x unit weight, or a sum of those) is a float, so
 * it is first written at 15 significant digits — the precision a double
 * actually holds — which discards float noise at both ends of the scale
 * (3 x 0.0045 = 0.013499999999999998 -> "0.0135"; 50000 x 200.123456 =
 * 10006172.800000001 -> "10006172.8") and then trimmed the same way. A
 * 9-integer-digit weight with 6 decimals is exactly 15 digits, so nothing a
 * part can carry is lost, and the text matches the backend's exact Decimal.
 *
 * @param value - the API's decimal string, a number, or absent
 * @returns the plain decimal text, or '' when there is no numeric value
 */
export function plainDecimal(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  let text: string;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    text = value.toPrecision(15);
    // toPrecision falls back to exponent form below 1e-6; write those out.
    if (text.includes('e')) text = value.toFixed(12);
  } else {
    text = value.trim();
  }
  if (!/^-?\d*\.?\d+$/.test(text)) return '';
  const trimmed = text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
  return trimmed === '' || trimmed === '-' || trimmed === '-0' ? '0' : trimmed;
}
